<script lang="ts">
  // Small transparent Pixi canvas rendering the ENLARGED "main" avatar bust
  // (the customization/menu art). `headshot` crops + zooms tight to the face;
  // `windowed` frames the full bust (head + upper torso) inside the square like a
  // viewport peek; otherwise it shows the full waist-up bust. Counterpart to
  // ChibiPreview (which renders the tiny map paper-doll).
  import { onMount } from "svelte";
  import { Application } from "pixi.js";
  import { PortraitAvatar, type PortraitSelection } from "../../render/PortraitAvatar.ts";
  import { loadWorldData } from "../../data.ts";

  let {
    size = 220,
    look,
    headshot = false,
    windowed = false,
    mirrored = false,
    blink = false,
  }: {
    size?: number;
    look?: { gender: "cm" | "cw"; selection: PortraitSelection };
    headshot?: boolean;
    /** Frame the full bust (head + upper torso) inside the square, clipped, a "window" portrait. */
    windowed?: boolean;
    /** Mirror the canonical left-facing bust so it faces screen-right. */
    mirrored?: boolean;
    /** Idle eye-blink (duel busts only — keep HUD/menu portraits static). */
    blink?: boolean;
  } = $props();

  // Head region within the 768-tall portrait canvas (tuned so the face fills a
  // headshot box): vertical centre + the slice height that should fill `size`.
  const HEAD_CY = 150;
  const HEAD_H = 330;
  // "Window" framing: the canvas slice shown through the square, head + shoulders
  // read as a framed portrait (the head dominates; only a little chest shows).
  // Centred a touch below the face so the head isn't jammed against the top.
  const WIN_CY = 165;
  const WIN_H = 350;

  let host: HTMLDivElement;
  let app: Application | null = null;
  let av: PortraitAvatar | null = null;

  // `{ removeView: true }`, NOT `true`: `app.destroy(true)` calls Pixi's
  // GlobalResourceRegistry.release(), wiping the GLOBAL pools (TexturePool, batcher geometry)
  // shared by every live Pixi app, so destroying this bust (e.g. closing the menu) would blank
  // the still-mounted world canvas. removeView frees only this renderer + its canvas. The
  // try/catch is a belt-and-suspenders backstop so a teardown throw can't blank the app.
  function destroyApp() {
    try {
      app?.destroy({ removeView: true });
    } catch (e) {
      console.error("[PortraitPreview] destroy failed (ignored)", e);
    }
    app = null;
  }

  function frame() {
    if (!av) return;
    if (windowed) {
      const scale = size / WIN_H;
      av.root.scale.set(scale);
      av.root.x = size / 2 - 384 * scale; // figure is horizontally centred at x=384
      av.root.y = size / 2 - WIN_CY * scale;
    } else if (headshot) {
      const scale = size / HEAD_H;
      av.root.scale.set(scale);
      av.root.x = size / 2 - 384 * scale; // figure is horizontally centred at x=384
      av.root.y = size / 2 - HEAD_CY * scale;
    } else {
      const scale = size / 768;
      av.root.scale.set(scale);
      av.root.x = (size - 768 * scale) / 2;
      av.root.y = 0;
    }
  }

  $effect(() => {
    void [size, headshot, windowed, mirrored, look?.gender, JSON.stringify(look?.selection)];
    if (av && look) {
      av.set({ selection: look.selection, gender: look.gender, mirrored });
      frame();
    }
  });

  onMount(() => {
    let stopped = false;
    (async () => {
      const data = await loadWorldData();
      if (stopped) return;
      app = new Application();
      // Render at a higher backing resolution than the on-screen size so SMALL portraits (e.g. the
      // 32–48px LP-chip / HUD headshots) stay crisp instead of upscaling into a blurry, chibi-looking
      // blob. `autoDensity` keeps the CSS size at `size`; the stage stays in logical px so frame()
      // is unaffected. Big busts already have enough pixels, so resolution floors at the device DPR.
      const resolution = Math.min(4, Math.max(globalThis.devicePixelRatio || 1, Math.ceil(176 / size)));
      await app.init({ width: size, height: size, backgroundAlpha: 0, antialias: true, resolution, autoDensity: true, preference: "webgpu" });
      // The portrait is STATIC apart from the occasional blink — Pixi's default ticker re-rendered it
      // at full display rate (two 560px busts flank every duel = two wasted GPU passes per frame).
      // 12fps is imperceptible for a blink and gives those frames back to the board.
      app.ticker.maxFPS = 12;
      if (stopped) {
        destroyApp();
        return;
      }
      host.appendChild(app.canvas);
      const l = look ?? { gender: "cm" as const, selection: {} as PortraitSelection };
      av = new PortraitAvatar({
        manifest: data.avatarsPortrait,
        gender: l.gender,
        selection: l.selection,
        displayHeight: size,
        mirrored,
        blink,
      });
      await av.build();
      frame();
      app.stage.addChild(av.root);
    })();
    return () => {
      stopped = true;
      destroyApp();
    };
  });
</script>

<div bind:this={host} class="overflow-hidden" style="width:{size}px;height:{size}px"></div>
